<template>
  <v-card flat>
    <v-card-text v-if="readonly">
      <div>
        <div class="caption">{{ $t('event') }}</div>
        <p class="primary-color">
          {{
            intent.event_setting && intent.event_setting.event_name
              ? intent.event_setting.event_name
              : '-'
          }}
        </p>
      </div>
      <v-layout row wrap>
        <v-flex xs6>
          <div class="caption">{{ $t('Amount') }}</div>
          <p class="primary-color">
            <specialtext
              :text="
                intent.event_setting && intent.event_setting.value
                  ? intent.event_setting.value
                  : '-'
              "
            />
          </p>
        </v-flex>
        <v-flex xs6>
          <div class="caption">{{ $t('monetary_unit') }}</div>
          <p class="primary-color">
            {{
              intent.event_setting && intent.event_setting.currency
                ? intent.event_setting.currency
                : '-'
            }}
          </p>
        </v-flex>
        <v-flex xs6>
          <div class="caption">Content type</div>
          <p class="primary-color">
            {{
              intent.event_setting && intent.event_setting.content_type
                ? intent.event_setting.content_type
                : '-'
            }}
          </p>
        </v-flex>
        <v-flex xs6>
          <div class="caption">Content name</div>
          <p class="primary-color">
            {{
              intent.event_setting && intent.event_setting.content_name
                ? intent.event_setting.content_name
                : '-'
            }}
          </p>
        </v-flex>
      </v-layout>
    </v-card-text>
    <v-card-text v-else>
      <div class="mb-2 d-flex" style="align-items: center">
        <div :class="{ 'has-error': manualError('eventName') }" style="width: 100%">
          <div class="primary--text caption mb-1">{{ $t('event') }}</div>
          <v-select
            v-if="!isRendering"
            v-model="eventSetting.event_name"
            v-validate="{ required: eventSetting.value !== '' }"
            name="eventName"
            :items="eventNames"
            item-text="label"
            item-value="value"
            outline
            :data-vv-as="$t('event')"
            :data-vv-scope="intent.id"
            :disabled="isVerifySlip"
            single-line
            @change="onEventNameChanged"
          ></v-select>
          <span v-if="manualError('eventName')" class="red--text" style="font-size: 0.9em">{{
            manualTextError('eventName')
          }}</span>
        </div>
        <v-btn small icon @click="clearFieldEventSetting('event_name')">
          <v-icon>close</v-icon>
        </v-btn>
      </div>
      <v-layout row wrap>
        <v-flex xs6 px-1 d-flex>
          <div
            class="form-control"
            :class="{ 'has-error': manualError('eventSettingValue') }"
            style="width: 100%"
          >
            <div class="primary--text caption">{{ $t('Amount') }}</div>
            <input
              v-if="!isRendering"
              ref="eventSettingValue"
              v-model="eventSetting.value"
              v-validate="{
                required: Boolean(
                  eventSetting.event_name !== 'LeadSubmitted' &&
                    (eventSetting.event_name !== '' || eventSetting.currency)
                )
              }"
              type="text"
              class="form-control"
              name="eventSettingValue"
              :data-vv-as="$t('Amount')"
              :data-vv-scope="intent.id"
              :disabled="isVerifySlip || eventSetting.event_name === 'LeadSubmitted'"
            />
            <span
              v-if="manualError('eventSettingValue')"
              class="red--text"
              style="font-size: 0.9em"
              >{{ manualTextError('eventSettingValue') }}</span
            >
          </div>
          <v-menu v-if="!isVerifySlip" offset-y open-on-hover>
            <template #activator="{ on }">
              <v-btn
                fab
                small
                outline
                class="ml-1 extra-small"
                color="primary"
                :disabled="eventSetting.event_name === 'LeadSubmitted'"
                v-on="on"
              >
                <span class="fas fa-ellipsis-v"></span>
              </v-btn>
            </template>
            <v-list>
              <v-list-tile
                v-for="(prefill, index) in [{ label: $t('Number'), value: 'number' }]"
                :key="index"
                @click="() => insertEventPurchaseValuePrefill(prefill.value)"
              >
                <v-list-tile-title class="primary-color">{{ $t(prefill.label) }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-flex>
        <v-flex xs6 px-1 d-flex style="align-items: center">
          <div class="mb-2" :class="{ 'has-error': manualError('currencies') }" style="width: 100%">
            <div class="primary--text caption">
              {{ $t('monetary_unit') }}
              {{ eventSetting.event_name === 'Purchase' ? '*' : '' }}
            </div>
            <v-autocomplete
              v-if="!isRendering"
              ref="currency"
              v-model="eventSetting.currency"
              v-validate="{
                required: Boolean(eventSetting.event_name === 'Purchase' || eventSetting.value)
              }"
              name="currencies"
              :items="$store.state.currencies"
              :data-vv-as="$t('monetary_unit')"
              :data-vv-scope="intent.id"
              :error="manualError('currencies')"
              :disabled="eventSetting.event_name === 'LeadSubmitted'"
              hide-details
              outline
              single-line
            />
            <span v-if="manualError('currencies')" class="red--text" style="font-size: 0.9em">{{
              manualTextError('currencies')
            }}</span>
          </div>
          <v-btn small icon @click="clearFieldEventSetting('currency')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-flex>
        <v-flex xs6 class="px-1 d-flex align-center">
          <div
            class="mb-2"
            :class="{ 'has-error': manualError('content_type') }"
            style="width: 100%"
          >
            <div class="primary--text caption">
              Content type
              {{ eventSetting.event_name === 'ViewContent' ? '*' : '' }}
            </div>
            <v-autocomplete
              v-if="!isRendering"
              v-model="eventSetting.content_type"
              v-validate="{
                required: eventSetting.event_name === 'ViewContent'
              }"
              name="content_type"
              :items="['Product']"
              data-vv-as="Content Type"
              :data-vv-scope="intent.id"
              :error="manualError('content_type')"
              :disabled="eventSetting.event_name === 'LeadSubmitted'"
              hide-details
              outline
              single-line
            />
            <span v-if="manualError('content_type')" class="red--text" style="font-size: 0.9em">{{
              manualTextError('content_type')
            }}</span>
          </div>
          <v-btn small icon @click="clearFieldEventSetting('content_type')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-flex>
        <v-flex xs6 class="px-1 d-flex align-center">
          <div
            class="mb-2"
            :class="{ 'has-error': manualError('content_name') }"
            style="width: 100%"
          >
            <div class="primary--text caption">Content name</div>
            <v-text-field
              v-if="!isRerendering"
              ref="eventContentName"
              v-model="eventSetting.content_name"
              name="content_name"
              :items="['Product']"
              :data-vv-as="$t('content_name')"
              hide-details
              outline
              :data-vv-scope="intent.id"
              :error="manualError('content_name')"
              :disabled="eventSetting.event_name === 'LeadSubmitted'"
              single-line
              @input="handleContentName"
            />
          </div>
          <v-btn small icon @click="clearFieldEventSetting('content_name')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card-text>
  </v-card>
</template>

<script>
import Specialtext from '@/components/elements/specialtext.vue'

export default {
  components: {
    Specialtext
  },
  props: {
    intent: {
      type: Object,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      eventNames: [
        {
          label: 'View Content',
          value: 'ViewContent'
        },
        {
          label: 'Add to Cart',
          value: 'AddToCart'
        },
        {
          label: 'Purchase',
          value: 'Purchase'
        },
        {
          label: 'LeadSubmitted',
          value: 'LeadSubmitted'
        }
      ],
      isRendering: false,
      isRerendering: false
    }
  },
  computed: {
    eventSetting: {
      get() {
        if (this.intent.event_setting) return this.intent.event_setting

        return {
          event_name: '',
          value: '',
          currency: 'THB',
          content_type: '',
          content_name: ''
        }
      },
      set(value) {
        this.$emit('input', { ...this.intent, event_setting: value })
      }
    },
    isVerifySlip() {
      return this.intent.q_val.includes('{verify_slip.bank_info')
    }
  },
  methods: {
    async onEventNameChanged(value) {
      this.eventSetting = {
        ...this.eventSetting,
        event_name: value
      }
      this.$nextTick(() => {
        if (value === 'LeadSubmitted') {
          this.eventSetting = {
            ...this.eventSetting,
            value: '',
            currency: '',
            content_type: '',
            content_name: ''
          }
        }
      })

      this.isRerendering = true
      this.$nextTick(() => {
        this.isRerendering = false
      })
    },
    manualError(field) {
      const scope = this.intent.id || null
      const error = this.errors.items.find((item) => item.field === field && item.scope === scope)
      return Boolean(error)
    },
    manualTextError(field) {
      const scope = this.intent.id || null
      const error = this.errors.items.find((item) => item.field === field && item.scope === scope)
      if (error) {
        return error.msg
      } else {
        return ''
      }
    },
    clearFieldEventSetting(field) {
      if (!field) return
      this.isRendering = true
      this.$nextTick(() => {
        this.eventSetting = { ...this.eventSetting, [field]: null }
        this.isRendering = false
      })
    },
    insertEventPurchaseValuePrefill(value) {
      this.eventSetting = {
        ...this.eventSetting,
        value: `{${value}}`
      }
    },
    handleContentName(event) {
      this.isRerendering = true
      this.$nextTick(async () => {
        if (this.eventSetting.content_name) {
          this.eventSetting = {
            ...this.eventSetting,
            content_name: event.replace(/[^a-zA-Z]+/g, '')
          }
        }
        this.isRerendering = false

        await this.$nextTick()
        const eventContentName = this.$refs.eventContentName
        if (eventContentName) {
          eventContentName.focus()
        }
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/view/intent/PurchaseEventManagement.vue